/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { ClipboardCopyIcon } from '@heroicons/react/solid'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Card, Form as NXUIForm } from '@nexigendigital/nxui'
import { domainAPI, useToggleTransferLockMutation } from 'api/domain'
import { ServiceStatus } from 'models/enums'
import { EligibilityDetail } from 'models/domain'
import { setInvoice } from 'store/slices/billingSlice'
import { useAppDispatch } from 'store/hooks'
import { copyToClipboard } from 'helpers/utils'

import UpdateEPPCodeLightbox from 'containers/domains/lightbox/updateEPPCodeLightbox'
import RenewDomainLightbox from 'containers/domains/lightbox/renewDomainLightbox'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Information() {
    const dispatch = useAppDispatch()
    const location = useLocation()
    const id = +location.pathname?.split('/')[2]
    const [openUpdateEPPCodeLightbox, setOpenUpdateEPPCodeLightbox] = useState(false)
    const [openRenewDomainLightbox, setOpenRenewDomainLightbox] = useState(false)
    const { data: service } = domainAPI.endpoints.domain.useQueryState(id)
    const { data: eppCode } = domainAPI.endpoints.eppCode.useQueryState(id)
    const { data: eligibilityDetails = [] } = domainAPI.endpoints.eligibilityDetails.useQueryState(id)
    const { data: domainRenewal } = domainAPI.endpoints.domainRenewal.useQueryState(id)
    const [toggleTransferLock] = useToggleTransferLockMutation()
    const generalColumn = useMemo(() => ([
        {
            Header: 'General Information',
            accessor: 'information'
        },
        {
            accessor: 'value'
        },
        {
            Header: '',
            accessor: 'action',
            align: 'right'
        }
    ]), [])
    const generalData = useMemo(() => {
        const domain = service ? service?.domain.split('.') : ['']

        if (domain[domain.length - 1] === 'au') {
            return ([
                {
                    information: 'Renew Domain Name',
                    value: (
                        <Renewal>
                            <span>
                                {domainRenewal?.errors[0]}
                            </span>
                            <Anchor color={'primary'} onClick={() => dispatch(setInvoice(domainRenewal?.invoice?.id))}>
                                {domainRenewal?.invoice?.id ? `#${domainRenewal?.invoice?.id}` : null}
                            </Anchor>
                        </Renewal>
                    ),
                    action: (
                        domainRenewal?.statuses.is_renewable
                            ? (

                                <Anchor
                                    color={'primary'}
                                    onClick={() => setOpenRenewDomainLightbox(true)}
                                >
                                    Renew
                                </Anchor>
                            )
                            : null
                    )
                },
                {
                    information: 'EPP Code',
                    value: (
                        <EPPCode>
                            <span>{eppCode}</span>
                            {service?.status === ServiceStatus.Active
                                ? (
                                    <button onClick={() => copyToClipboard(eppCode)}>
                                        <ClipboardCopyIcon />
                                    </button>
                                )
                                : null
                            }
                        </EPPCode>
                    ),
                    action: (
                        service?.status === ServiceStatus.Pending
                            ? <Anchor color={'primary'} onClick={() => setOpenUpdateEPPCodeLightbox(true)}>Update</Anchor>
                            : null
                    )
                }
            ])
        }

        return ([
            {
                information: 'Renew Domain Name',
                value: (
                    <Renewal>
                        <span>
                            {domainRenewal?.errors[0]}
                        </span>
                        <Anchor color={'primary'} onClick={() => dispatch(setInvoice(domainRenewal?.invoice?.id))}>
                            {domainRenewal?.invoice?.id ? `#${domainRenewal?.invoice?.id}` : null}
                        </Anchor>
                    </Renewal>
                ),
                action: (
                    domainRenewal?.statuses.is_renewable
                        ? (

                            <Anchor
                                color={'primary'}
                                onClick={() => setOpenRenewDomainLightbox(true)}
                            >
                                Renew
                            </Anchor>
                        )
                        : null
                )
            },
            {
                information: 'Transfer Lock',
                value: (
                    <NXUIForm.Switch
                        checked={!!service?.meta.transfer_lock}
                        onClick={() => {
                            if (service) {
                                toggleTransferLock({
                                    id,
                                    domain: {
                                        ...service,
                                        meta: {
                                            ...service?.meta,
                                            transfer_lock: !service?.meta.transfer_lock
                                        }
                                    }
                                })
                            }
                        }}
                    />
                )
            },
            {
                information: 'EPP Code',
                value: (
                    <EPPCode>
                        <span>{eppCode}</span>
                        <button onClick={() => copyToClipboard(eppCode)}>
                            <ClipboardCopyIcon />
                        </button>
                    </EPPCode>
                ),
                action: (
                    service?.status === ServiceStatus.Pending
                        ? <Anchor color={'primary'} onClick={() => setOpenUpdateEPPCodeLightbox(true)}>Update</Anchor>
                        : null
                )
            }
        ])
    }, [eppCode, service, domainRenewal])
    const eligibilityColumn = useMemo(() => ([
        {
            Header: 'Eligibility Information',
            accessor: 'information'
        },
        {
            accessor: 'value'
        }
    ]), [])
    const eligibilityData = useMemo(() => eligibilityDetails.map(({ field_name, field_value }: EligibilityDetail) => ({
        information: field_name,
        value: field_value
    })), [eligibilityDetails])

    return (
        <InformationWrapper>
            <Card
                table={{
                    render: {
                        columns: () => generalColumn,
                        data: () => generalData
                    }
                }}
            />
            {eligibilityDetails.length > 0
                ? (
                    <Card
                        table={{
                            render: {
                                columns: () => eligibilityColumn,
                                data: () => eligibilityData
                            }
                        }}
                    />
                )
                : null
            }
            <UpdateEPPCodeLightbox
                id={id}
                status={openUpdateEPPCodeLightbox}
                close={() => setOpenUpdateEPPCodeLightbox(false)}
            />
            <RenewDomainLightbox
                id={id}
                status={openRenewDomainLightbox}
                close={() => setOpenRenewDomainLightbox(false)}
            />
        </InformationWrapper>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const InformationWrapper = styled.div`
    width: 100%;
`

const EPPCode = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    button {
        > svg {
            width: 24px;
            height: 24px;
        }
    }
`

const Renewal = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 0.875rem;

    > button {
        font-size: 0.875rem;
    }
`
